import {isNil} from 'ramda';

import type {ReportDocumentSection} from './ChartReportDocument';

const MONTH_CATEGORY = /^\d{4}-(0[1-9]|1[0-2])$/;
const MAX_COMBINED_COLUMNS = 5;

export function createReportDocumentTables(group: {
  sections: ReportDocumentSection[];
  combineTables: boolean;
}) {
  const firstSection = group.sections[0];
  if (isNil(firstSection)) {
    return [];
  }

  const canCombine =
    group.combineTables &&
    group.sections.every(
      (section) =>
        section.categoryType === 'month' &&
        section.categories.length === firstSection.categories.length &&
        section.categories.every((category, index) => category === firstSection.categories[index])
    ) &&
    group.sections.reduce(
      (count, section) => count + (section.tableColumns ?? section.series).length,
      0
    ) <= MAX_COMBINED_COLUMNS;

  const tableSections = canCombine ? [group.sections] : group.sections.map((section) => [section]);
  return tableSections.flatMap((sections) => {
    const first = sections[0];
    if (isNil(first)) {
      return [];
    }
    const columns = sections.flatMap((section) =>
      (section.tableColumns ?? section.series).map((column) => {
        const summary = section.summaryItems?.find(
          (item) => item.label === column.label && item.kind === column.kind
        );
        return {
          ...column,
          key: `${section.id}-${column.id}`,
          summary:
            isNil(summary) || column.summaryPercentage === undefined
              ? summary
              : {...summary, percentage: column.summaryPercentage},
        };
      })
    );
    if (columns.length === 0 || first.categories.length === 0) {
      return [];
    }
    const isMonthly =
      first.categoryType === 'month' &&
      first.categories.every((category) => MONTH_CATEGORY.test(category));
    const rows = first.categories.map((category, index) => ({
      category,
      index,
      values: columns.map((column) => column.values[index] ?? null),
      incomplete: sections.some((section) => section.incompleteCategories?.includes(category)),
    }));
    return [
      {
        id: sections.map((section) => section.id).join('-'),
        title: sections.map((section) => section.title).join(' / '),
        columns,
        isMonthly,
        rows: isMonthly
          ? rows.toSorted((left, right) => right.category.localeCompare(left.category))
          : rows,
      },
    ];
  });
}
